import { Link } from "@/lib/router-compat";
import {
  PenTool,
  HardHat,
  Hammer,
  Zap,
  Lightbulb,
  Monitor,
  Sparkles,
  RefreshCw,
  ArrowRight,
} from "lucide-react";

const CORE_SERVICES = [
  {
    icon: PenTool,
    title: "Interior Design",
    slug: "interior-design",
    body: "Concept development and creative planning for offices, retail, F&B, hospitality and residential spaces - executing the vision exactly as envisioned.",
    link_label: "Learn More →",
  },
  {
    icon: HardHat,
    title: "Fit-Out & Turnkey Contracting",
    slug: "fit-out-contracting",
    body: "Our flagship service: end-to-end delivery of commercial interiors, from demolition to handover, managed by a single accountable team.",
    link_label: "Learn More →",
  },
  {
    icon: Hammer,
    title: "Joinery & Custom Furniture",
    slug: "custom-joinery",
    body: "In-house state-of-the-art joinery unit producing bespoke furniture, doors, wall panelling, counters and ceilings - crafted to millimetre precision.",
    link_label: "Learn More →",
  },
  {
    icon: Zap,
    title: "MEP Solutions",
    slug: "mep-contracting",
    body: "Design, installation and maintenance of electrical, plumbing and HVAC systems - fully integrated with the fit-out programme.",
    link_label: "Learn More →",
  },
];

const SPECIALIST_SERVICES = [
  {
    icon: Lightbulb,
    title: "Lighting - Abacus Luminaire",
    slug: "architectural-lighting",
    body: 'Design and manufacture of bespoke lighting and LED solutions. "Design is a subtle alchemy - lighting is its key."',
    link_label: "Learn More →",
  },
  {
    icon: Monitor,
    title: "Digital Solutions",
    slug: "digital-signage-display",
    body: "Digital kiosks, video walls, Mirror TVs, LED tickers and large-format graphics that turn spaces into brand experiences.",
    link_label: "Learn More →",
  },
  {
    icon: Sparkles,
    title: "Signage & Decorative Elements",
    slug: "signage-decorative-metalwork",
    body: "Indoor and outdoor signage, metal decorative work, wooden carving, CNC / 3D panels and architectural embellishments.",
    link_label: "Learn More →",
  },
  {
    icon: RefreshCw,
    title: "Renovation & Refurbishment",
    slug: "commercial-renovation-refurbishment",
    body: "Modernising existing spaces - shops, offices and restaurants - with minimal disruption to your operations.",
    link_label: "Learn More →",
  },
];

export default function ServicesSection() {
  return (
    <section
      className="py-16 md:py-24 bg-[#002b5c] text-white overflow-hidden"
      id="services"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <span className="inline-block text-[#ffcc29] font-heading font-bold text-base md:text-lg lg:text-xl tracking-[0.2em] uppercase mb-3 sm:mb-4">
            OUR EXPERTISE
          </span>
          <h2 className="text-white font-heading text-3xl sm:text-5xl md:text-6xl font-bold leading-tight uppercase tracking-tight mb-4 sm:mb-6">
            One Team. From First Sketch to Final Handover.
          </h2>
          <p className="text-white/80 text-base sm:text-lg font-body leading-relaxed">
            Design → Manufacture → Install → Deliver. As a complete one-stop
            interior solution provider, we control every stage in-house - no
            subcontractor roulette, no compromises.
          </p>
        </div>

        {/* Core Services Group */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6 pb-2 border-b border-white/10">
            <span className="text-[#ffcc29] font-heading font-bold text-lg tracking-[0.2em] uppercase">
              01
            </span>
            <h3 className="font-heading text-xl font-bold text-white uppercase tracking-wider">
              Core Services
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {CORE_SERVICES.map((service) => (
              <div key={service.slug}>
                <Link
                  to={`/services/${service.slug}`}
                  className="group p-8 sm:p-10 border border-white/10 hover:bg-white hover:text-[#002b5c] transition-all duration-500 cursor-pointer flex flex-col justify-between h-full"
                >
                  <div>
                    <service.icon className="w-10 h-10 text-[#ffcc29] mb-6 transition-transform group-hover:scale-110" />
                    <h4 className="font-heading text-2xl font-bold mb-4 uppercase tracking-wide leading-tight">
                      {service.title}
                    </h4>
                    <p className="text-white/70 group-hover:text-[#002b5c]/80 text-sm font-body leading-relaxed mb-8">
                      {service.body}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 font-bold text-sm text-[#ffcc29] group-hover:text-[#002b5c] transition-colors">
                    LEARN MORE{" "}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Specialist Services Group */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6 pb-2 border-b border-white/10">
            <span className="text-[#ffcc29] font-heading font-bold text-lg tracking-[0.2em] uppercase">
              02
            </span>
            <h3 className="font-heading text-xl font-bold text-white uppercase tracking-wider">
              Specialist Divisions
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {SPECIALIST_SERVICES.map((service) => (
              <div key={service.slug}>
                <Link
                  to={`/services/${service.slug}`}
                  className="group p-8 sm:p-10 border border-white/10 hover:bg-white hover:text-[#002b5c] transition-all duration-500 cursor-pointer flex flex-col justify-between h-full"
                >
                  <div>
                    <service.icon className="w-10 h-10 text-[#ffcc29] mb-6 transition-transform group-hover:scale-110" />
                    <h4 className="font-heading text-2xl font-bold mb-4 uppercase tracking-wide leading-tight">
                      {service.title}
                    </h4>
                    <p className="text-white/70 group-hover:text-[#002b5c]/80 text-sm font-body leading-relaxed mb-8">
                      {service.body}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 font-bold text-sm text-[#ffcc29] group-hover:text-[#002b5c] transition-colors">
                    LEARN MORE{" "}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Section CTA */}
        <div className="mt-12 bg-white/5 border border-white/10 p-8 sm:p-12 text-center md:flex md:items-center md:justify-between md:text-left rounded-xs">
          <div>
            <h3 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white mb-2">
              Planning to open, expand or renovate?
            </h3>
            <p className="text-white/70 font-body text-sm sm:text-base">
              Get in touch with our team of specialists to bring your
              architectural vision to life.
            </p>
          </div>
          <Link
            to="/contact"
            className="mt-6 md:mt-0 inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#ffcc29] text-[#002b5c] font-heading font-bold text-sm tracking-wider uppercase hover:bg-white transition-colors duration-300 shadow-lg"
          >
            Start Your Project <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
